import {
  GridColDef,
  GridColumnResizeParams,
  gridColumnFieldsSelector,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import {
  initializeColumnWidths,
  initializeColumns,
  updateColumnOrder,
  updateColumnWidth,
} from "store/slices/tableSlice";
import { useAppDispatch, useAppSelector } from "store/store";

interface UseColumnsStateProps {
  columns: readonly GridColDef[];
  name: string;
}

export const useColumnsState = ({ columns, name }: UseColumnsStateProps) => {
  const apiRef = useGridApiRef();
  const dispatch = useAppDispatch();
  const columnsOrder = useAppSelector((state) => state?.table?.columnsOrder);
  const columnsWidth = useAppSelector((state) => state?.table?.columnsWidth);
  const orderedFields = columnsOrder[name] || [];
  const columnWidths = columnsWidth[name] || {};

  useEffect(() => {
    if (columns.length > 0 && orderedFields.length !== columns.length) {
      dispatch(
        initializeColumns({
          name,
          fields: columns.map((column) => column.field),
        }),
      );
    }

    // Initialize column widths if they don't exist yet
    if (Object.keys(columnWidths).length === 0 && columns.length > 0) {
      const initialWidths: Record<string, number> = {};
      columns.forEach((column) => {
        // Use width if available, otherwise use minWidth
        if (column.width) {
          initialWidths[column.field] = column.width;
        }
        // else if (column.minWidth) {
        //   initialWidths[column.field] = column.minWidth;
        // }
      });

      if (Object.keys(initialWidths).length > 0) {
        dispatch(
          initializeColumnWidths({
            name,
            widths: initialWidths,
          }),
        );
      }
    }
  }, [columns, orderedFields, columnWidths, dispatch, name]);

  const onColumnOrderChange = useCallback(() => {
    const currentState = apiRef.current.state;
    const newOrderedFields = gridColumnFieldsSelector(currentState);
    dispatch(
      updateColumnOrder({
        name,
        fields: newOrderedFields,
      }),
    );
  }, [apiRef, dispatch, name]);

  // Create a debounced version of the column width update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateWidth = useCallback(
    debounce((field: string, width: number, doubleClick: boolean) => {
      dispatch(
        updateColumnWidth({
          name,
          field,
          width,
          doubleClick,
        }),
      );
    }, 200), // Lower debounce time for Redux updates
    [dispatch, name],
  );

  // This handler will be called on every resize event
  const onColumnWidthChange = useCallback(
    ({ colDef, width }: GridColumnResizeParams) => {
      // Debounce the Redux update to avoid lag
      debouncedUpdateWidth(colDef.field, width, false);

      // After resize, make sure all columns are properly displayed
      // by refreshing the grid layout
      setTimeout(() => {
        if (apiRef.current) {
          // Force the DataGrid to recompute layouts
          apiRef.current.forceUpdate();
        }
      }, 0);
    },
    [debouncedUpdateWidth, apiRef],
  );

  const onColumnCellDoubleClickWidthChange = useCallback(
    ({ colDef }: GridColumnResizeParams) => {
      // Debounce the Redux update to avoid lag
      const findColumn = columns.find(
        (column) => column.field === colDef.field,
      );

      debouncedUpdateWidth(colDef.field, findColumn?.minWidth, true);
      // After resize, make sure all columns are properly displayed
      // by refreshing the grid layout
      setTimeout(() => {
        if (apiRef.current) {
          // Force the DataGrid to recompute layouts
          apiRef.current.forceUpdate();
        }
      }, 0);
    },
    [debouncedUpdateWidth, apiRef],
  );

  // const computedColumns = useMemo(() => {
  //   const processColumn = (column: GridColDef) => {
  //     const { field } = column;

  //     if (columnWidths[field]) {
  //       // Create a completely new column object to avoid reference issues
  //       const newColumn = {
  //         ...column,
  //         // Force width to override any existing width
  //         width: columnWidths[field],
  //         // Set flex to 0 to ensure width takes precedence
  //         flex: 0,
  //       };
  //       // Explicitly remove minWidth to prevent it from interfering with width
  //       // delete newColumn.minWidth;
  //       return newColumn;
  //     }

  //     // For columns without explicit width, use flex to fill available space
  //     return {
  //       ...column,
  //       flex: 1,
  //       // Apply a minimum width to prevent columns from shrinking too much
  //       minWidth: column.minWidth || 100,
  //     };
  //   };

  //   // Apply ordering and width processing
  //   if (orderedFields.length > 0) {
  //     return orderedFields.reduce<GridColDef[]>((acc, field) => {
  //       const column = columns.find((col) => col.field === field);
  //       if (!column) return acc;
  //       acc.push(processColumn(column));
  //       return acc;
  //     }, []);
  //   }

  //   // Just apply width processing to original columns
  //   return columns.map(processColumn);
  // }, [columns, orderedFields, columnWidths]);

  const computedColumns = useMemo(() => {
    const processColumn = (column: GridColDef) => {
      const { field } = column;

      if (columnWidths[field]) {
        return {
          ...column,
          width: columnWidths[field],
          flex: 0,
        };
      }

      return {
        ...column,
        flex: 1,
        minWidth: column.minWidth || 100,
      };
    };

    const orderedSet = new Set(orderedFields);

    // Columns that are present in orderedFields
    const orderedColumns = orderedFields
      .map((field) => columns.find((col) => col.field === field))
      .filter(Boolean) // remove undefined
      .map(processColumn);

    // Columns that are not in orderedFields (e.g., newly added columns)
    const remainingColumns = columns
      .filter((col) => !orderedSet.has(col.field))
      .map(processColumn);

    return [...orderedColumns, ...remainingColumns];
  }, [columns, orderedFields, columnWidths]);

  return {
    apiRef,
    columns: computedColumns,
    onColumnOrderChange,
    onColumnWidthChange,
    onColumnCellDoubleClickWidthChange,
  };
};
